<template>
  <el-container class="page">
    <AppSidebar :active-menu.sync="activeMenu" />

    <el-container>
      <el-header class="header">
        <div class="header-left">
          <i class="el-icon-data-analysis"></i>
          <span class="header-title">数据管理大屏</span>
        </div>

        <div class="header-right">
          <el-button icon="el-icon-refresh" @click="onRefresh">刷新数据</el-button>
        </div>
      </el-header>

      <el-main class="main">
        <div class="tabs">
          <div class="tabs-inner">
            <span class="tabs-label">日期选择：</span>
            <!-- 数据管理大屏日期筛选：使用系统设置中的入园日期选项 -->
            <el-select
              v-model="selectedVisitDate"
              placeholder="全部数据"
              size="small"
              clearable
              class="tabs-select"
            >
              <el-option label="全部数据" value="" />
              <el-option
                v-for="(d, idx) in historyDates"
                :key="idx"
                :label="formatHistoryLabel(d)"
                :value="d"
              />
            </el-select>
          </div>
        </div>

        <el-row :gutter="12" class="metric-row">
          <el-col :span="4" v-for="m in metrics" :key="m.key">
            <div class="metric-card">
              <div class="metric-icon">
                <i :class="m.icon"></i>
              </div>
              <div class="metric-content">
                <div class="metric-title">{{ m.title }}</div>
                <div class="metric-value">{{ m.value }}</div>
                <div class="metric-sub">{{ m.sub }}</div>
              </div>
            </div>
          </el-col>
        </el-row>

        <el-row :gutter="12" class="stat-row">
          <el-col :span="4" v-for="s in stats" :key="s.key">
            <div class="stat-card">
              <div class="stat-title">{{ s.title }}</div>
              <div class="stat-value">{{ s.value }}</div>
              <div class="stat-sub" :class="s.danger ? 'danger' : ''">{{ s.sub }}</div>
            </div>
          </el-col>
        </el-row>

        <!-- 图表区 -->
        <el-row :gutter="12">
          <el-col :span="14">
            <el-card shadow="never" class="chart-card">
              <div class="chart-title">观礼平台分析</div>
              <div class="chart-sub">各平台游客数量占比</div>
              <div class="chart-box">
                <BaseEchart :option="platformPieOption" />
              </div>
            </el-card>
          </el-col>
          <el-col :span="10">
            <el-card shadow="never" class="chart-card">
              <div class="chart-title">财务数据概览</div>
              <div class="chart-sub">收入、成本与利润对比</div>
              <div class="chart-box">
                <BaseEchart :option="financeBarOption" />
              </div>
            </el-card>
          </el-col>
        </el-row>

        <el-row :gutter="12">
          <el-col :span="14">
            <el-card shadow="never" class="chart-card">
              <div class="chart-title">达人与非达人订单对比</div>
              <div class="chart-sub">订单数量与平均利润对比</div>
              <div class="chart-box">
                <BaseEchart :option="talentCompareOption" />
              </div>
            </el-card>
          </el-col>
          <el-col :span="10">
            <el-card shadow="never" class="chart-card">
              <div class="chart-title">月度核销趋势</div>
              <div class="chart-sub">近30天核销变化趋势</div>
              <div class="chart-box">
                <BaseEchart :option="verifyLineOption" />
              </div>
            </el-card>
          </el-col>
        </el-row>

        <el-row :gutter="12">
          <el-col :span="24">
            <el-card shadow="never" class="chart-card">
              <div class="chart-title">渠道来源分析</div>
              <div class="chart-sub">各渠道订单数量与平均利润</div>
              <div class="chart-box chart-box-lg">
                <BaseEchart :option="channelBarLineOption" />
              </div>
            </el-card>
          </el-col>
        </el-row>
        

        <el-row :gutter="12">
          <el-col :span="24">
            <el-card shadow="never" class="chart-card">
              <div class="chart-title">票种类型分布</div>
              <div class="chart-sub">各观礼平台的票种类型分布</div>
              <div class="chart-box chart-box-lg">
                <BaseEchart :option="ticketStackOption" />
              </div>
            </el-card>
          </el-col>
        </el-row>

        <!-- 新增：人口统计（成人/儿童 & 男女）可视化 -->
        <el-row :gutter="12">
          <el-col :span="12">
            <el-card shadow="never" class="chart-card">
              <div class="chart-title">成人 / 儿童 性别对比</div>
              <div class="chart-sub">{{ demographicSubtitle }}</div>
              <div class="chart-box">
                <BaseEchart :option="demographicBarOption" />
              </div>
            </el-card>
          </el-col>
          <el-col :span="12">
            <el-card shadow="never" class="chart-card">
              <div class="chart-title">年龄/性别分布（细化）</div>
              <div class="chart-sub">{{ demographicSubtitle }}</div>
              <div class="chart-box">
                <BaseEchart :option="demographicPieOption" />
              </div>
            </el-card>
          </el-col>
        </el-row>

        <!-- 详细数据列表区域已按请求移除 -->
      </el-main>
    </el-container>
  </el-container>
</template>

<script>
import AppSidebar from '../../components/AppSidebar.vue'
import BaseEchart from '../../components/Echart.vue'
import { getDashboardSummary, getVisitors, getSettings, getPlatformCounts, getPlatformTicketTypes, getChannelCounts, getDemographicCounts, getFinanceSummary, getTalentCompare, getVerifyTrend } from '../../api'
import { PLATFORMS } from '../../config/platforms'

const toNumber = v => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

const sum = arr => arr.reduce((a, b) => a + b, 0)

// Infer gender from Chinese ID number: returns 'male'|'female'|'unknown'
const inferGenderFromIdNo = idNo => {
  if (!idNo) return 'unknown'
  try {
    const s = String(idNo).trim()
    // remove non-digits (in case of spaces or formatting)
    const digits = s.replace(/[^0-9Xx]/g, '')
    // 18-digit ID: the 17th char (index 16) indicates gender (odd=male)
    if (digits.length === 18) {
      const v = parseInt(digits.charAt(16), 10)
      if (Number.isFinite(v)) return (v % 2 === 1) ? 'male' : 'female'
    }
    // 15-digit ID: 15th char (index 14)
    if (digits.length === 15) {
      const v = parseInt(digits.charAt(14), 10)
      if (Number.isFinite(v)) return (v % 2 === 1) ? 'male' : 'female'
    }
  } catch (e) {
    // fallthrough
  }
  return 'unknown'
}

export default {
  name: 'DataManagePage',
  components: { AppSidebar, BaseEchart },
  data() {
    return {
      activeMenu: '5',
      activeTab: 'all',
      loading: false,
      summary: {
        totalVisitors: 0,
        totalOrders: 0,
        totalRetail: 0,
        totalProfit: 0,
        avgProfitRate: '0%',
        verifyRate: '0%',
        ticketStats: { adult: 0, child: 0, free: 0 },
        verifyStats: { verified: 0, unverified: 0 },
        platformStats: []
      },
      tableData: [],
      // platform colors from system settings: { platformName: '#rrggbb' }
      platformColors: {},
      // platform options (use hardcoded platforms to stay consistent with录入页)
      platformOptions: PLATFORMS.slice(),
      // cache for mapping backend platform names to display keys
      platformMappingCache: {},
      // ticket type options and colors from system settings
      ticketTypeOptions: [],
      ticketTypeColors: {},
      // visitor channel options and colors from system settings
      channelOptions: [],
      channelColors: {},
      // 系统设置中的入园日期选项（用于大屏日期筛选）
      historyDates: [],
      // 当前选中的入园日期；为空表示“全部数据”
      selectedVisitDate: ''
    }
  },
  computed: {
    // 1) 观礼平台分析（饼图）
    platformPieOption() {
      const stats = (this.summary && this.summary.platformStats) || []
      const defaultPalette = ['#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399', '#1610DE']

      // prefer platformOptions from system settings; fallback to names present in stats
      const platforms = (Array.isArray(this.platformOptions) && this.platformOptions.length)
        ? this.platformOptions.slice()
        : Array.from(new Set(stats.map(s => s.name)))

      // Build counts by mapping backend stat names to our display platforms
      const counts = {}
      platforms.forEach(p => { counts[p] = 0 })
      ;(stats || []).forEach(s => {
        const rawName = s && s.name
        const displayKey = this.findDisplayPlatformKey(rawName)
        if (displayKey && Object.prototype.hasOwnProperty.call(counts, displayKey)) {
          counts[displayKey] = (counts[displayKey] || 0) + Number(s.value || 0)
        }
      })
      let data = platforms.map(p => ({ name: p, value: counts[p] || 0 }))

      // Per request: do NOT fallback to tableData when backend summary is absent.
      // Keep zeros if backend did not produce platform counts.

      // Note: do not aggregate unknown platforms into "其他" — only show configured platforms

      const colors = data.map((d, i) => {
        return (this.platformColors && this.platformColors[d.name]) ? this.platformColors[d.name] : defaultPalette[i % defaultPalette.length]
      })

      return {
        color: colors,
        tooltip: { trigger: 'item' },
        legend: { bottom: 0, left: 'center' },
        series: [
          {
            type: 'pie',
            radius: ['35%', '60%'],
            center: ['50%', '45%'],
            label: { formatter: '{b}: {c}' },
            data
          }
        ]
      }
    },

    // 2) 财务数据概览（柱状）
    financeBarOption() {
      const retailSum = Number(this.summary.totalRetail || 0)
      // 当前后端 summary 没单独返回成本/提成汇总，这里先用 0 占位（后续可扩展接口字段）
      const settleSum = 0
      const commissionSum = 0
      const profit = Number(this.summary.totalProfit || 0)

      return {
        tooltip: { trigger: 'axis' },
        grid: { left: 40, right: 10, top: 10, bottom: 30 },
        xAxis: { type: 'category', data: ['收入', '成本', '提成', '利润'] },
        yAxis: { type: 'value' },
        series: [
          {
            type: 'bar',
            barWidth: 26,
            data: [
              { value: retailSum, itemStyle: { color: '#409EFF' } },
              { value: settleSum, itemStyle: { color: '#E6A23C' } },
              { value: commissionSum, itemStyle: { color: '#F56C6C' } },
              { value: profit, itemStyle: { color: '#67C23A' } }
            ]
          }
        ]
      }
    },

    // 3) 达人 vs 非达人（订单数柱状 + 平均利润折线）
    talentCompareOption() {
      // Prefer backend-provided aggregated data when available to avoid tableData pagination limits
      const backend = this.summary && this.summary.talentCompare ? this.summary.talentCompare : null

      let talentCount = 0
      let normalCount = 0
      let talentAvg = 0
      let normalAvg = 0

      if (backend && typeof backend === 'object' && (backend.talent || backend.normal)) {
        talentCount = Number((backend.talent && backend.talent.count) || 0)
        normalCount = Number((backend.normal && backend.normal.count) || 0)
        talentAvg = Number((backend.talent && backend.talent.avgProfit) || 0)
        normalAvg = Number((backend.normal && backend.normal.avgProfit) || 0)
      } else {
        // Fallback to client-side calculation from tableData (may be paginated/limited)
        const rows = this.tableData || []
        const talentRows = rows.filter(x => x.talent)
        const normalRows = rows.filter(x => !x.talent)

        const calcProfit = list => {
          const retail = sum(list.map(x => toNumber(x.retailPrice)))
          const settle = sum(list.map(x => toNumber(x.settlementPrice)))
          const commission = sum(
            list.map(x => {
              const rate = toNumber(String(x.commissionRate || '').replace('%', '')) / 100
              return toNumber(x.settlementPrice) * rate
            })
          )
          return retail - settle - commission
        }

        const avgProfit = list => {
          if (!list.length) return 0
          return Math.round((calcProfit(list) / list.length) * 100) / 100
        }

        talentCount = talentRows.length
        normalCount = normalRows.length
        talentAvg = avgProfit(talentRows)
        normalAvg = avgProfit(normalRows)
      }

      return {
        tooltip: { trigger: 'axis' },
        legend: { bottom: 0 },
        grid: { left: 40, right: 40, top: 10, bottom: 30 },
        xAxis: { type: 'category', data: ['达人订单', '非达人订单'] },
        yAxis: [
          { type: 'value', name: '订单数' },
          { type: 'value', name: '平均利润' }
        ],
        series: [
          {
            name: '订单数',
            type: 'bar',
            data: [talentCount, normalCount],
            itemStyle: { color: '#409EFF' },
            barWidth: 26
          },
          {
            name: '平均利润',
            type: 'line',
            yAxisIndex: 1,
            data: [talentAvg, normalAvg],
            itemStyle: { color: '#67C23A' },
            smooth: true
          }
        ]
      }
    },

    // 4) 月度核销趋势（折线）
    verifyLineOption() {
      // 用最近 30 天做横轴（示例），真实场景你用后端统计
      const days = Array.from({ length: 30 }).map((_, i) => {
        const d = new Date()
        d.setDate(d.getDate() - (29 - i))
        const mm = String(d.getMonth() + 1).padStart(2, '0')
        const dd = String(d.getDate()).padStart(2, '0')
        return `${mm}-${dd}`
      })

      // 简易示例：把已核销数量平均分布到 30 天（避免全 0）
      const total = this.tableData.length
      const verified = this.tableData.filter(x => x.verifyStatus === '已核销').length
      const perDay = total ? verified / 30 : 0
      const data = days.map((_, i) => Math.round(perDay * (i / 29) * 100) / 100)

      return {
        tooltip: { trigger: 'axis' },
        grid: { left: 40, right: 10, top: 10, bottom: 30 },
        xAxis: { type: 'category', data: days, axisLabel: { interval: 5 } },
        yAxis: { type: 'value' },
        series: [
          {
            type: 'line',
            smooth: true,
            data,
            areaStyle: { opacity: 0.15 },
            itemStyle: { color: '#409EFF' }
          }
        ]
      }
    },

    // 5) 渠道来源分析（柱状：订单数 + 折线：平均利润）——使用系统设置中的渠道选项
    channelBarLineOption() {
      // Use backend-provided channelCounts when available (aggregated by canonical key)
      const backendChannelCounts = (this.summary && this.summary.channelCounts) ? this.summary.channelCounts : {}

      // normalize channel values (support both english keys and chinese labels)
      const normalizeChannel = v => {
        const s = (v || '').toString().trim().toLowerCase()
        if (!s) return '其他'
        // substring matching to be tolerant to variations (e.g. "线下支付宝", "梦宇抖音平台")
        if (s.includes('online') || s.includes('线上') || s.includes('抖音') || s.includes('平台')) return 'online'
        if (s.includes('offline') || s.includes('线下') || s.includes('线下渠道') || s.includes('支付宝') || s.includes('微信')) return 'offline'
        if (s.includes('agency') || s.includes('旅行') || s.includes('旅游') || s.includes('团队') || s.includes('社') || s.includes('旅行社')) return 'agency'
        return '其他'
      }

      // Build labels as UNION(original system-configured labels, backend rawChannelCounts keys)
      // Preserve order: system settings first, then backend-only raw keys.
      const originalLabels = Array.isArray(this.channelOptions) && this.channelOptions.length ? this.channelOptions.slice() : []
      const rawChannelCounts = (this.summary && this.summary.rawChannelCounts) ? this.summary.rawChannelCounts : {}
      const labels = []
      const seen = new Set()
      for (const l of originalLabels) {
        if (!seen.has(l)) {
          seen.add(l)
          labels.push(l)
        }
      }
      for (const rk of Object.keys(rawChannelCounts)) {
        if (!seen.has(rk)) {
          seen.add(rk)
          labels.push(rk)
        }
      }
      let channels = labels.map(l => normalizeChannel(l))
      if (!labels.length) {
        channels = ['online', 'offline']
        labels.push('线上', '线下')
      }

      // For each displayed label prefer the backend rawChannelCounts (exact text match).
      // Fallback to normalized backendChannelCounts (canonical key) when exact match missing.
      // Assign counts: prefer exact raw counts. When falling back to canonical backend counts,
      // only assign each canonical key once to avoid duplicating the same total across multiple labels.
      const assignedCanonical = new Set()
      const orderCount = labels.map(label => {
        const rawKey = label
        if (Object.prototype.hasOwnProperty.call(rawChannelCounts, rawKey)) {
          return Number(rawChannelCounts[rawKey] || 0)
        }
        const chKey = normalizeChannel(label)
        if (assignedCanonical.has(chKey)) {
          return 0
        }
        assignedCanonical.add(chKey)
        return Number(backendChannelCounts[chKey] || 0)
      })
      // avgProfit not available from backend currently; fallback to 0
      const avgProfit = channels.map(() => 0)

      return {
        tooltip: { trigger: 'axis' },
        legend: { bottom: 0 },
        grid: { left: 40, right: 40, top: 10, bottom: 30 },
        xAxis: { type: 'category', data: labels },
        yAxis: [
          { type: 'value', name: '订单数' },
          { type: 'value', name: '平均利润' }
        ],
        series: [
          {
            name: '订单数量',
            type: 'bar',
            barWidth: 26,
            data: orderCount,
            itemStyle: { color: '#409EFF' }
          },
          {
            name: '平均利润',
            type: 'line',
            yAxisIndex: 1,
            data: avgProfit,
            smooth: true,
            itemStyle: { color: '#E6A23C' }
          }
        ]
      }
    },

    // 6) 票种类型分布（堆叠柱状：按平台分组）
    ticketStackOption() {
      const rows = this.tableData || []

      // Prefer backend's platformTicketStats if present
      const backendPTS = (this.summary && this.summary.platformTicketStats) || []

      // determine platforms: always use configured platformOptions
      const platforms = (Array.isArray(this.platformOptions) && this.platformOptions.length) ? this.platformOptions.slice() : []

      // determine ticket types: start from configured options, then include any backend keys not already represented.
      const normalizeTicket = s => {
        if (!s && s !== 0) return ''
        return String(s).trim().replace(/\s+/g, ' ').toLowerCase().replace(/票$/i, '')
      }

      const backendKeys = new Set()
      if (backendPTS && backendPTS.length) {
        backendPTS.forEach(p => {
          const tickets = p.tickets || {}
          Object.keys(tickets).forEach(k => {
            if (k) backendKeys.add(k)
          })
        })
      }

      let ticketTypes = (Array.isArray(this.ticketTypeOptions) && this.ticketTypeOptions.length) ? this.ticketTypeOptions.slice() : []
      // append backend keys that are not already represented (by normalized equality)
      backendKeys.forEach(k => {
        const exists = ticketTypes.some(t => normalizeTicket(t) === normalizeTicket(k))
        if (!exists) ticketTypes.push(k)
      })

      // If still empty, fallback to ticket types discovered from rows
      if (!ticketTypes.length) {
        ticketTypes = Array.from(new Set(rows.map(r => r.ticketType).filter(Boolean)))
      }

      const defaultPalette = ['#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399', '#1610DE']

      // If backend provided platformTicketStats, use it directly to build series
      let series = []
      if (backendPTS && backendPTS.length) {
        // build a map platform -> tickets
        const map = {}
        backendPTS.forEach(p => {
          const name = p.platform || ''
          map[name] = p.tickets || {}
        })
        series = ticketTypes.map((tt, idx) => {
          const normT = normalizeTicket(tt)
          const data = platforms.map(p => {
            const m = map[p] || {}
            // try direct match first, then normalized match against backend keys
            if (Object.prototype.hasOwnProperty.call(m, tt)) return Number(m[tt] || 0)
            for (const bk of Object.keys(m)) {
              if (normalizeTicket(bk) === normT) return Number(m[bk] || 0)
            }
            return 0
          })
          const color = (this.ticketTypeColors && this.ticketTypeColors[tt]) ? this.ticketTypeColors[tt] : defaultPalette[idx % defaultPalette.length]
          return { name: tt, type: 'bar', stack: 'total', data, itemStyle: { color } }
        })
      } else {
        // Per request: do NOT compute from rows when backend data is missing.
        // Instead return zeroed series for ticketTypes across configured platforms.
        series = ticketTypes.map((tt, idx) => {
          const data = platforms.map(() => 0)
          const color = (this.ticketTypeColors && this.ticketTypeColors[tt]) ? this.ticketTypeColors[tt] : defaultPalette[idx % defaultPalette.length]
          return { name: tt, type: 'bar', stack: 'total', data, itemStyle: { color } }
        })
      }

      return {
        tooltip: { trigger: 'axis' },
        legend: { bottom: 0 },
        grid: { left: 40, right: 10, top: 10, bottom: 30 },
        xAxis: { type: 'category', data: platforms },
        yAxis: { type: 'value' },
        series
      }
    },

    // 人口统计相关计算：成人/儿童按年龄（>=18成人），并从身份证号推断性别（男/女）
    demographicCounts() {
      // prefer server-provided demographic counts when available (computed on backend)
      const sdc = (this.summary && this.summary.demographicCounts) ? this.summary.demographicCounts : null
      if (sdc && typeof sdc === 'object' && !Array.isArray(sdc)) {
        // ensure expected keys exist; fallback to zeros if missing
        return {
          adultMale: Number(sdc['adultMale'] ?? 0),
          adultFemale: Number(sdc['adultFemale'] ?? 0),
          childMale: Number(sdc['childMale'] ?? 0),
          childFemale: Number(sdc['childFemale'] ?? 0),
          total: Number(sdc['total'] ?? 0)
        }
      }
      // fallback: compute from tableData (client-side)
      const rows = this.tableData || []
      const counts = { adultMale: 0, adultFemale: 0, childMale: 0, childFemale: 0, total: rows.length }
      for (const r of rows) {
        const age = toNumber(r.age)
        const idNo = r.idNo || r.identityNo || r.identity || ''
        // prefer explicit gender field if backend provides it
        const explicitGender = (r.gender || r.sex || r.sexual || '').toString().trim().toLowerCase()
        let gender = 'unknown'
        if (explicitGender === 'male' || explicitGender === 'm' || explicitGender === '男') gender = 'male'
        else if (explicitGender === 'female' || explicitGender === 'f' || explicitGender === '女') gender = 'female'
        else gender = inferGenderFromIdNo(idNo)

        const hasValidAge = Number.isFinite(age) && age > 0
        const isAdult = hasValidAge ? age >= 18 : null

        if (gender === 'male') {
          if (isAdult === true) counts.adultMale += 1
          else if (isAdult === false) counts.childMale += 1
          else counts.adultMale += 1 // default unknown -> adult male
        } else if (gender === 'female') {
          if (isAdult === true) counts.adultFemale += 1
          else if (isAdult === false) counts.childFemale += 1
          else counts.adultFemale += 1 // default unknown -> adult female
        } else {
          // default unknown -> adult male
          counts.adultMale += 1
        }
      }
      return counts
    },

    demographicBarOption() {
      const c = this.demographicCounts || { adultMale: 0, adultFemale: 0, childMale: 0, childFemale: 0 }
      return {
        tooltip: { trigger: 'axis' },
        legend: { bottom: 0 },
        grid: { left: 40, right: 20, top: 20, bottom: 30 },
        xAxis: { type: 'category', data: ['成人', '儿童'] },
        yAxis: { type: 'value' },
        series: [
          { name: '男', type: 'bar', data: [c.adultMale || 0, c.childMale || 0], itemStyle: { color: '#409EFF' }, barWidth: 26 },
          { name: '女', type: 'bar', data: [c.adultFemale || 0, c.childFemale || 0], itemStyle: { color: '#E86A91' }, barWidth: 26 }
        ]
      }
    },

    demographicPieOption() {
      const c = this.demographicCounts || { adultMale: 0, adultFemale: 0, childMale: 0, childFemale: 0, total: 0 }
      const data = [
        { name: '成人-男', value: c.adultMale || 0 },
        { name: '成人-女', value: c.adultFemale || 0 },
        { name: '儿童-男', value: c.childMale || 0 },
        { name: '儿童-女', value: c.childFemale || 0 }
      ]
      const colors = ['#409EFF', '#E86A91', '#67C23A', '#F6A23C']
      return {
        color: colors,
        tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        legend: { bottom: 0, left: 'center' },
        series: [
          { type: 'pie', radius: ['35%', '60%'], center: ['50%', '45%'], label: { formatter: '{b}: {c}' }, data }
        ]
      }
    },
    demographicSubtitle() {
      const c = this.demographicCounts || { total: 0 }
      const total = c.total || 0
      return `已统计 ${total} 条`
    },

    metrics() {
      const s = this.summary || {}
      const fmtMoney = n => `¥${Number(n || 0).toFixed(2)}`
      return [
        { key: 'total', title: '游客总数', value: s.totalVisitors || 0, sub: '人', icon: 'el-icon-user' },
        { key: 'order', title: '订单数量', value: s.totalOrders || 0, sub: '单', icon: 'el-icon-document' },
        { key: 'retail', title: '总收入', value: fmtMoney(s.totalRetail), sub: '零售合计', icon: 'el-icon-money' },
        { key: 'profit', title: '总利润', value: fmtMoney(s.totalProfit), sub: '利润合计', icon: 'el-icon-s-finance' },
        { key: 'rate', title: '平均利润率', value: s.avgProfitRate || '0%', sub: '所有订单平均', icon: 'el-icon-data-line' },
        { key: 'verify', title: '核销率', value: s.verifyRate || '0%', sub: '核销率', icon: 'el-icon-circle-check' }
      ]
    },
    stats() {
      const s = this.summary || {}
      return [
        { key: 'adult', title: '成人票数量', value: (s.ticketStats && s.ticketStats.adult) || 0, sub: '统计口径：当前筛选' },
        { key: 'child', title: '儿童票数量', value: (s.ticketStats && s.ticketStats.child) || 0, sub: '统计口径：当前筛选' },
        { key: 'free', title: '免票数量', value: (s.ticketStats && s.ticketStats.free) || 0, sub: '统计口径：当前筛选' },
        { key: 'verify_yes', title: '已核销', value: (s.verifyStats && s.verifyStats.verified) || 0, sub: '核销数据', danger: false },
        { key: 'verify_no', title: '未核销', value: (s.verifyStats && s.verifyStats.unverified) || 0, sub: '核销数据', danger: true },
        { key: 'today', title: '今日录入', value: (s.todayCount || 0), sub: '' }
      ]
    },
    tableDataComputed() {
      return this.tableData
    }
  },
  created() {
    this.loadAll()
    // listen for cross-page data updates (e.g. import on Finance page)
    this._onDataUpdated = () => {
      this.loadAll()
    }
    window.addEventListener('dataUpdated', this._onDataUpdated)
  },
  beforeDestroy() {
    if (this._onDataUpdated) window.removeEventListener('dataUpdated', this._onDataUpdated)
  },
  watch: {
    // 当选择的入园日期变更时，重新加载整套数据
    selectedVisitDate() {
      this.loadAll()
    }
  },
  methods: {
    // normalize platform/name strings for matching
    normalizeName(s) {
      if (!s && s !== 0) return ''
      return String(s).trim().replace(/\s+/g, ' ').toLowerCase()
    },

    // Find the display platform key (from this.platformOptions) that best matches the raw backend/platform value.
    // Uses cache to avoid repeated work.
    findDisplayPlatformKey(raw) {
      const key = String(raw || '')
      if (this.platformMappingCache && Object.prototype.hasOwnProperty.call(this.platformMappingCache, key)) {
        return this.platformMappingCache[key]
      }
      const normRaw = this.normalizeName(raw)
      let found = null
      // 1) exact match (case/space-insensitive)
      for (const p of this.platformOptions || []) {
        if (this.normalizeName(p) === normRaw) {
          found = p
          break
        }
      }
      // 2) raw includes display or display includes raw (fuzzy)
      if (!found) {
        for (const p of this.platformOptions || []) {
          const np = this.normalizeName(p)
          if (!np) continue
          if (np.includes(normRaw) || normRaw.includes(np)) {
            found = p
            break
          }
        }
      }
      // cache result (could be null)
      if (!this.platformMappingCache) this.platformMappingCache = {}
      this.$set(this.platformMappingCache, key, found)
      return found
    },
    // 通用参数构造：若选择了具体入园日期，则按该日期统计；否则按全部数据统计
    buildDashboardParams() {
      if (this.selectedVisitDate) {
        return { visitDate: this.selectedVisitDate }
      }
      return { range: 'all' }
    },
    // 历史日期下拉展示格式（与其它页面保持一致）
    formatHistoryLabel(d) {
      try {
        const s = String(d).replace(/\//g, '-')
        const dt = new Date(s)
        if (isNaN(dt.getTime())) return d
        const y = dt.getFullYear()
        const m = String(dt.getMonth() + 1).padStart(2, '0')
        const day = String(dt.getDate()).padStart(2, '0')
        return `${y}.${m}.${day}`
      } catch (e) {
        return d
      }
    },
    async loadSummary() {
      this.loading = true
      try {
        const params = this.buildDashboardParams()
        const [data, platformData, ticketData, channelData] = await Promise.all([
          getDashboardSummary(params),
          getPlatformCounts(params),
          // ticket types per platform
          getPlatformTicketTypes(),
          // channel counts dedicated endpoint
          getChannelCounts(params)
        ])

        this.summary = { ...this.summary, ...(data || {}) }
        // override/add channel data from dedicated endpoint if present
        if (channelData && typeof channelData === 'object') {
          this.summary.channelCounts = channelData.channelCounts || {}
          this.summary.rawChannelCounts = channelData.rawChannelCounts || {}
        }
        if (platformData && Array.isArray(platformData.platformCounts)) {
          this.summary.platformStats = platformData.platformCounts || []
        } else {
          this.summary.platformStats = this.summary.platformStats || []
        }

        // Transform ticketData (object map) into backendPTS shape expected by ticketStackOption:
        // [{ platform: '天航平台', tickets: { '成人票': 12, ... } }, ...]
        try {
          if (ticketData && typeof ticketData === 'object') {
            const pts = []
            const platforms = (Array.isArray(this.platformOptions) && this.platformOptions.length) ? this.platformOptions : []
            // ensure we iterate fixed platforms (keep order)
            for (const p of platforms) {
              const raw = ticketData[p]
              // normalize: backend may return array or object; we expect an object mapping ticketType -> count
              let ticketsObj = {}
              if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
                // copy and coerce values to numbers
                Object.keys(raw).forEach(k => {
                  ticketsObj[k] = Number(raw[k] || 0)
                })
              }
              pts.push({ platform: p, tickets: ticketsObj })
            }
            this.summary.platformTicketStats = pts
          } else {
            this.summary.platformTicketStats = this.summary.platformTicketStats || []
          }
        } catch (e) {
          this.summary.platformTicketStats = this.summary.platformTicketStats || []
        }
      } catch (e) {
        this.$message.error(e.message || '加载统计失败')
      } finally {
        this.loading = false
      }
    },
    async loadTable() {
      try {
        const data = await getVisitors({ page: 1, pageSize: 200 })
        this.tableData = data.list || []
      } catch (e) {
        this.$message.error(e.message || '加载明细失败')
      }
    },
    async loadAll() {
      this.loading = true
      try {
        const params = this.buildDashboardParams()
        const [summaryData, platformData, ticketData, channelData, demData, financeData, talentData, verifyData] = await Promise.all([
          getDashboardSummary(params),
          getPlatformCounts(params),
          getPlatformTicketTypes(),
          getChannelCounts(params),
          getDemographicCounts(params),
          getFinanceSummary(params),
          getTalentCompare(params),
          getVerifyTrend({ days: 30 })
        ])

        this.summary = { ...this.summary, ...(summaryData || {}) }
        if (platformData && Array.isArray(platformData.platformCounts)) {
          this.summary.platformStats = platformData.platformCounts || []
        }
        // ticket types
        try {
          if (ticketData && typeof ticketData === 'object') {
            const pts = []
            const platforms = (Array.isArray(this.platformOptions) && this.platformOptions.length) ? this.platformOptions : []
            for (const p of platforms) {
              const raw = ticketData[p]
              let ticketsObj = {}
              if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
                Object.keys(raw).forEach(k => {
                  ticketsObj[k] = Number(raw[k] || 0)
                })
              }
              pts.push({ platform: p, tickets: ticketsObj })
            }
            this.summary.platformTicketStats = pts
          } else {
            this.summary.platformTicketStats = this.summary.platformTicketStats || []
          }
        } catch (e) {
          this.summary.platformTicketStats = this.summary.platformTicketStats || []
        }

        // channel / demographic / finance / talent / verify
        if (channelData && typeof channelData === 'object') {
          this.summary.channelCounts = channelData.channelCounts || {}
          this.summary.rawChannelCounts = channelData.rawChannelCounts || {}
        }
        if (demData) this.summary.demographicCounts = demData
        if (financeData) {
          this.summary.totalRetail = financeData.totalRetail || 0
          this.summary.totalProfit = financeData.totalProfit || 0
          this.summary.avgProfitRate = financeData.avgProfitRate || '0%'
        }
        if (talentData) this.summary.talentCompare = talentData
        if (verifyData) this.summary.verifyTrend = verifyData
      } catch (e) {
        this.$message.error(e.message || '加载统计失败')
      } finally {
        // also load table and settings in parallel
        await Promise.all([this.loadTable(), this.loadSettings()])
        this.loading = false
      }
    },
    async loadSettings() {
      try {
        const data = await getSettings()
        const visitor = (data && data.visitor) || {}
        this.platformColors = visitor.platformColors || {}
        // Use hardcoded platforms to stay consistent with 录入页 (do not rely on backend platformOptions)
        this.platformOptions = PLATFORMS.slice()
        this.ticketTypeOptions = Array.isArray(visitor.ticketTypeOptions) ? visitor.ticketTypeOptions : (visitor.ticketTypeOptions || [])
        this.ticketTypeColors = visitor.ticketTypeColors || {}
        // load channel options/colors from system settings so charts use the same values
        this.channelOptions = Array.isArray(visitor.channelOptions) ? visitor.channelOptions : (visitor.channelOptions || [])
        this.channelColors = visitor.channelColors || {}
        // 系统设置中的入园日期选项，用于大屏日期筛选
        this.historyDates = visitor.visitDates || []
        // dedicated channel-options UI removed; keep channel options from general settings only
      } catch (e) {
        this.platformColors = {}
        this.platformOptions = []
        this.ticketTypeOptions = []
        this.ticketTypeColors = {}
        this.channelOptions = []
        this.channelColors = {}
        this.historyDates = []
      }
    },
    // import disabled
    async onRefresh() {
      await this.loadAll()
      this.$message.success('已刷新')
    }
  }
}
</script>

<style scoped>
.page {
  height: 100vh;
  background: #f5f7fa;
}

.header {
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #ebeef5;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #303133;
}

.header-title {
  font-size: 18px;
  font-weight: 700;
}

.main {
  padding: 16px;
}

.tabs {
  padding: 6px 12px 12px;
  margin-bottom: 8px;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #ebeef5;
}

.tabs-inner {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.tabs-label {
  font-size: 13px;
  color: #606266;
}

.tabs-select {
  width: 220px;
}

.metric-row {
  margin-bottom: 10px;
}

.metric-card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 10px;
  display: flex;
  gap: 10px;
  align-items: center;
  height: 72px;
}

.metric-icon {
  width: 38px;
  height: 38px;
  border-radius: 6px;
  background: #ecf5ff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #409eff;
  font-size: 18px;
}

.metric-title {
  font-size: 12px;
  color: #606266;
}

.metric-value {
  margin-top: 2px;
  font-size: 18px;
  font-weight: 800;
  color: #303133;
}

.metric-sub {
  margin-top: 2px;
  font-size: 12px;
  color: #909399;
}

.stat-row {
  margin-bottom: 12px;
}

.stat-card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 10px;
  height: 78px;
}

.stat-title {
  font-size: 12px;
  color: #606266;
}

.stat-value {
  margin-top: 6px;
  font-size: 18px;
  font-weight: 800;
  color: #303133;
}

.stat-sub {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}

.stat-sub.danger {
  color: #f56c6c;
}

.chart-card {
  margin-bottom: 12px;
}

.chart-title {
  font-weight: 700;
  color: #303133;
}

.chart-sub {
  margin-top: 2px;
  font-size: 12px;
  color: #909399;
  margin-bottom: 8px;
}

.chart-box {
  height: 180px;
}

.chart-box-lg {
  height: 220px;
}

.chart-box :deep(.chart-container) {
  height: 100%;
}

.table-card {
  position: relative;
}

.table-title {
  font-weight: 700;
  margin-bottom: 10px;
}

.empty {
  position: absolute;
  left: 0;
  right: 0;
  top: 55%;
  transform: translateY(-20%);
  text-align: center;
  color: #909399;
}

.empty-icon {
  font-size: 56px;
  color: #dcdfe6;
}

.empty-title {
  margin-top: 10px;
  font-size: 16px;
  font-weight: 700;
}

.empty-sub {
  margin-top: 6px;
  font-size: 12px;
}
</style>

